import React, { useEffect, useState } from "react";
import { api, type Me } from "./api";
import { Section } from "./VisitEscalation";
import { stamp } from "./Pass";

/**
 * The visit page's Insurance section (build plan §11.1 #3): an injury only.
 * The notice clock (amber within 14 days of due, red past it), the
 * precautionary notice (sent when a System Admin approves it on #17), Part I
 * for the program's signer, and the family packet (prepared here, emailed
 * from #17 by an RN or Supervisor, or printed).
 */

interface Insurance {
  visitKey: string;
  applies: boolean;
  outsideCare: boolean;
  status: string;
  dueOn: string;
  alert: "overdue" | "soon" | "";
  noticeSentUtc: string;
  packetSentUtc: string;
  notice: { notificationKey: string; status: string } | null;
  packets: { docKey: string; uploadedAtUtc: string; superseded: boolean; email: { notificationKey: string; status: string } | null }[];
  canPrepare: boolean;
  canApprove: boolean;
}

const EMAIL_STATUS: Record<string, string> = { Draft: "drafted: to release in #17", Approved: "released, not sent", Sent: "sent", Withdrawn: "withdrawn" };

export function VisitInsurance({ visitKey, me }: { visitKey: string; me: Me }): React.ReactElement | null {
  const [ins, setIns] = useState<Insurance | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void api<{ insurance: Insurance }>("GET", `/api/insurance/visits/${encodeURIComponent(visitKey)}`).then((r) => r.ok && setIns(r.data.insurance));
  }, [visitKey]);
  if (!ins || !ins.applies) return null;
  async function prepare() {
    setBusy(true);
    setError(null);
    const r = await api<{ insurance: Insurance }>("POST", `/api/insurance/visits/${encodeURIComponent(visitKey)}/packet`, {});
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not prepared.");
    setIns(r.data.insurance);
  }
  const current = ins.packets.find((p) => !p.superseded);
  const clock = ins.status === "Sent" ? `Notice sent ${stamp(ins.noticeSentUtc)}` : ins.status === "Due" ? `Notice due ${ins.dueOn}` : ins.outsideCare ? "Notice not needed" : "No outside care: no notice needed";
  const summary = `${clock}${ins.packetSentUtc ? ` · packet sent ${stamp(ins.packetSentUtc)}` : current ? " · packet prepared" : ""}`;
  return (
    <Section title="Insurance" summary={summary} warn={ins.alert !== ""} accent={ins.alert === "overdue" ? "border-red-600" : ins.alert === "soon" ? "border-amber-400" : ""}>
      {ins.alert && <p className={`rounded px-2 py-1 text-sm font-semibold ${ins.alert === "overdue" ? "bg-red-600 text-white" : "bg-amber-200 text-amber-950"}`}>{ins.alert === "overdue" ? `The notice was due ${ins.dueOn}: it is overdue.` : `The notice is due ${ins.dueOn}.`}</p>}
      <div className="text-sm">
        <p className="font-medium">Precautionary notice of claim</p>
        {!ins.outsideCare ? (
          <p className="text-slate-600">Only an injury with outside care needs one. Record the outside care on the form and a notice is drafted.</p>
        ) : ins.status === "Sent" ? (
          <p>Sent to the claims administrator {stamp(ins.noticeSentUtc)}. The text as sent is in the documents (Claim notice).</p>
        ) : ins.notice ? (
          <p>Drafted ({ins.notice.status}). {ins.canApprove ? "Approve and send it" : "A System Admin approves and sends it"} on <a className="text-program underline" href="/reports/17?tab=insurance">#17, Insurance notices</a>.</p>
        ) : (
          <p className="text-slate-600">Not drafted yet: save the visit.</p>
        )}
      </div>
      <div className="text-sm">
        <p className="font-medium">Claim form and family packet</p>
        <p className="text-slate-600">Part I is the program's to sign. The packet is the cover letter, the carrier's filing instructions and the claim form (Part I filled, Part II pre-filled; the Social Security Number boxes left for the family).</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {me.capabilities.includes("export.phi") && <a className="btn-secondary inline-flex min-h-11 items-center" href={`/api/insurance/visits/${encodeURIComponent(visitKey)}/part1`}>Part I (PDF)</a>}
          {ins.canPrepare && <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void prepare()}>{current ? "Prepare the packet again" : "Prepare the family packet"}</button>}
        </div>
        {current && (
          <p className="mt-2">
            <a className="text-program underline" href={`/api/med-documents/${encodeURIComponent(current.docKey)}`} target="_blank" rel="noreferrer">The packet (PDF)</a> · prepared {stamp(current.uploadedAtUtc)}
            {current.email && <> · email {EMAIL_STATUS[current.email.status] ?? current.email.status}{current.email.status !== "Sent" && <> (<a className="text-program underline" href="/reports/17">#17</a>)</>}</>}
          </p>
        )}
        {error && <p className="field-error" role="alert">{error}</p>}
      </div>
    </Section>
  );
}
